function ensureRoundRect(ctx) {
    if (!('roundRect' in ctx)) {
        ctx.roundRect = function (x, y, w, h, r) {
            const radii = Array.isArray(r) ? r : [r, r, r, r];
            const [r1, r2, r3, r4] = radii.map((v) => Math.max(0, Math.min(v, Math.min(w, h) / 2)));
            this.moveTo(x + r1, y);
            this.arcTo(x + w, y, x + w, y + h, r2);
            this.arcTo(x + w, y + h, x, y + h, r3);
            this.arcTo(x, y + h, x, y, r4);
            this.arcTo(x, y, x + w, y, r1);
            this.closePath();
        };
    }
}
export function starPath(ctx, cx, cy, r, pts, inner) {
    const step = Math.PI / pts;
    ctx.moveTo(cx, cy - r);
    for (let i = 0; i < pts * 2; i++) {
        const rad = i % 2 === 0 ? r : inner;
        const a = i * step - Math.PI / 2;
        ctx.lineTo(cx + rad * Math.cos(a), cy + rad * Math.sin(a));
    }
    ctx.closePath();
}
export function heartPath(ctx, cx, cy, s) {
    ctx.moveTo(cx, cy + s * 0.25);
    ctx.bezierCurveTo(cx - s * 0.5, cy - s * 0.1, cx - s * 0.5, cy - s * 0.5, cx, cy - s * 0.2);
    ctx.bezierCurveTo(cx + s * 0.5, cy - s * 0.5, cx + s * 0.5, cy - s * 0.1, cx, cy + s * 0.25);
    ctx.closePath();
}
export function drawDot(ctx, x, y, s, style, round) {
    ensureRoundRect(ctx);
    const cx = x + s / 2;
    const cy = y + s / 2;
    ctx.beginPath();
    switch (style) {
        case 'square':
            ctx.rect(x, y, s, s);
            break;
        case 'squarernd':
            ctx.roundRect(x, y, s, s, s * 0.18);
            break;
        case 'rounded':
            ctx.roundRect(x, y, s, s, s * round);
            break;
        case 'dot':
            ctx.arc(cx, cy, s / 2, 0, Math.PI * 2);
            break;
        case 'liquid':
            ctx.roundRect(x, y, s, s, s * 0.45);
            break;
        case 'arrow':
            ctx.moveTo(x, y);
            ctx.lineTo(x + s * 0.6, y);
            ctx.lineTo(x + s, cy);
            ctx.lineTo(x + s * 0.6, y + s);
            ctx.lineTo(x, y + s);
            ctx.lineTo(x + s * 0.4, cy);
            ctx.closePath();
            break;
        case 'heart':
            heartPath(ctx, cx, cy - s * 0.05, s * 0.52);
            break;
        case 'star':
            starPath(ctx, cx, cy, s * 0.5, 5, s * 0.22);
            break;
        case 'triangle':
            ctx.moveTo(cx, y);
            ctx.lineTo(x + s, y + s);
            ctx.lineTo(x, y + s);
            ctx.closePath();
            break;
        case 'block': {
            const t = s * 0.32;
            ctx.roundRect(x + t, y, s - t * 2, s, s * 0.08);
            ctx.rect(x, y + t, s, s - t * 2);
            break;
        }
        default:
            ctx.rect(x, y, s, s);
    }
    ctx.fill();
}
/**
 * Draws the OUTER eye + INNER "hole" using even-odd fill.
 * For 'square', this creates:
 * - outer 7x7 filled
 * - inner 5x5 cutout
 */
export function drawEyeShape(ctx, ox, oy, cell, style) {
    ensureRoundRect(ctx);
    const sz = 7 * cell;
    ctx.beginPath();
    if (style === 'square') {
        ctx.rect(ox, oy, sz, sz);
        ctx.rect(ox + cell, oy + cell, 5 * cell, 5 * cell);
    }
    else if (style === 'rounded') {
        ctx.roundRect(ox, oy, sz, sz, cell * 0.9);
        ctx.roundRect(ox + cell, oy + cell, 5 * cell, 5 * cell, cell * 0.55);
    }
    else if (style === 'circle') {
        ctx.arc(ox + sz / 2, oy + sz / 2, sz / 2, 0, Math.PI * 2);
        ctx.arc(ox + sz / 2, oy + sz / 2, sz / 2 - cell, 0, Math.PI * 2);
    }
    else if (style === 'leaf') {
        ctx.roundRect(ox, oy, sz, sz, [sz * 0.45, 0, sz * 0.45, 0]);
        ctx.roundRect(ox + cell, oy + cell, 5 * cell, 5 * cell, [sz * 0.25, 0, sz * 0.25, 0]);
    }
    else if (style === 'diamond') {
        const cx = ox + sz / 2;
        const cy = oy + sz / 2;
        ctx.moveTo(cx, oy);
        ctx.lineTo(ox + sz, cy);
        ctx.lineTo(cx, oy + sz);
        ctx.lineTo(ox, cy);
        ctx.closePath();
        ctx.moveTo(cx, oy + cell);
        ctx.lineTo(ox + sz - cell, cy);
        ctx.lineTo(cx, oy + sz - cell);
        ctx.lineTo(ox + cell, cy);
        ctx.closePath();
    }
}
/**
 * IMPORTANT FOR SCANNABILITY:
 * When pupil style is 'square', we force a strict QR-standard 3x3 pupil:
 * - no scale
 * - no rotation
 * - exactly at (2,2) inside the 7x7 finder
 *
 * iPhone camera detection is especially sensitive to finder geometry.
 */
export function drawPupil(ctx, ox, oy, cell, style, pupilScale, rotDeg) {
    ensureRoundRect(ctx);
    // ✅ QR-standard pupil (best compatibility)
    if (style === 'square') {
        const ix = ox + 2 * cell;
        const iy = oy + 2 * cell;
        const sz = 3 * cell;
        ctx.beginPath();
        ctx.rect(ix, iy, sz, sz);
        ctx.fill();
        return;
    }
    // Fancy pupils (can reduce scanning reliability)
    const baseSz = 3 * cell;
    const sz = baseSz * pupilScale;
    const ix = ox + 2 * cell + (baseSz - sz) / 2;
    const iy = oy + 2 * cell + (baseSz - sz) / 2;
    const cx = ix + sz / 2;
    const cy = iy + sz / 2;
    const r = sz / 2;
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate((rotDeg * Math.PI) / 180);
    ctx.translate(-cx, -cy);
    ctx.beginPath();
    switch (style) {
        case 'rounded':
            ctx.roundRect(ix, iy, sz, sz, sz * 0.25);
            break;
        case 'circle':
            ctx.arc(cx, cy, r, 0, Math.PI * 2);
            break;
        case 'halfleaf':
            ctx.roundRect(ix, iy, sz, sz, [0, r * 0.9, 0, r * 0.9]);
            break;
        case 'crystal':
            ctx.moveTo(cx, iy);
            ctx.lineTo(ix + sz * 0.8, iy + sz * 0.2);
            ctx.lineTo(ix + sz * 0.8, iy + sz * 0.8);
            ctx.lineTo(cx, iy + sz);
            ctx.lineTo(ix + sz * 0.2, iy + sz * 0.8);
            ctx.lineTo(ix + sz * 0.2, iy + sz * 0.2);
            ctx.closePath();
            break;
        case 'shield':
            ctx.moveTo(cx, iy);
            ctx.lineTo(ix + sz, iy + sz * 0.35);
            ctx.lineTo(ix + sz * 0.78, iy + sz);
            ctx.lineTo(ix + sz * 0.22, iy + sz);
            ctx.lineTo(ix, iy + sz * 0.35);
            ctx.closePath();
            break;
        case 'cross': {
            const t = sz * 0.3;
            ctx.roundRect(cx - t / 2, iy, t, sz, t * 0.2);
            ctx.rect(ix, cy - t / 2, sz, t);
            break;
        }
        case 'star':
            starPath(ctx, cx, cy, r, 5, r * 0.42);
            break;
        case 'diamond':
            ctx.moveTo(cx, iy);
            ctx.lineTo(ix + sz, cy);
            ctx.lineTo(cx, iy + sz);
            ctx.lineTo(ix, cy);
            ctx.closePath();
            break;
        case 'pulse':
            ctx.arc(cx, cy, r, 0, Math.PI * 2);
            ctx.arc(cx, cy, r * 0.45, 0, Math.PI * 2);
            ctx.fill('evenodd');
            ctx.restore();
            return;
        default:
            ctx.rect(ix, iy, sz, sz);
    }
    ctx.fill();
    ctx.restore();
}
